/* cart-sample-guard.js
 * Blind Barrels – prevent free bonus kit / included items checkout without subscription.
 * Spec: _specs/026-p1-high-priority-fixes.md (Issues #3, #20)
 *
 * DEFENSE-IN-DEPTH: This guard runs client-side only. It is NOT a security
 * boundary — a user who disables JavaScript or calls the Shopify AJAX API
 * directly can bypass it. Server-side enforcement via Shopify Scripts (Plus
 * feature) is recommended as a future hardening step.
 *
 * IDs for free / paid kits + subscriptions come from bbGuardConfig in localStorage,
 * which is written by the Subscription Plan Toggle section.
 *
 * Glass swap mapping (free → paid) reads from window.bbVariantConfig when
 * available (set by snippets/bb-variant-config.liquid), with hardcoded
 * fallbacks for resilience.
 */

(function () {
  const headersJSON = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };

  // Re-entrancy guard so ensureSampleGuard can't overlap runs
  let guardInFlight = false;

  // Map free glass variants → paid glass variants
  // Reads from central config (bb-variant-config.liquid) with hardcoded fallbacks.
  const cfg = window.bbVariantConfig || {};
  const GLASS_MAPPING = {
    // free single glass → paid single glass
    [cfg.quarterlyGlass || 44675174301903]: 44683708498127,
    // free 4-pack glass → paid 4-pack glass
    [cfg.legacyGlass || 44675174564047]: 44683694112975,
  };

  function loadGuardConfig() {
    if (typeof window === 'undefined') return null;

    // Primary source: localStorage (set by addon-bundler.js when user visits plan toggle)
    if (window.localStorage) {
      try {
        const raw = window.localStorage.getItem('bbGuardConfig');
        if (raw) {
          const stored = JSON.parse(raw);
          if (
            stored &&
            stored.freeSampleVariantId &&
            stored.paidSampleVariantId &&
            Array.isArray(stored.subscriptionVariantIds) &&
            stored.subscriptionVariantIds.length > 0
          ) {
            if (!Array.isArray(stored.freeGlassVariantIds)) {
              stored.freeGlassVariantIds = [];
            }
            return stored;
          }
        }
      } catch (e) {
        console.error('[sampleGuard] Failed to parse bbGuardConfig:', e);
      }
    }

    // Fallback: build config from server-rendered bbVariantConfig
    // This covers first-visit users who haven't seen the plan toggle yet.
    // See _specs/029-remaining-observations.md (Issue #33)
    var vc = window.bbVariantConfig;
    if (vc && vc.bonusBox && vc.annualSub) {
      return {
        freeSampleVariantId: vc.bonusBox,
        paidSampleVariantId: vc.bonusBox, // no paid equivalent; keep same
        subscriptionVariantIds: [vc.annualSub, vc.quarterlySub].filter(Boolean),
        freeGlassVariantIds: [vc.annualGlasses, vc.quarterlyGlass].filter(Boolean),
      };
    }

    return null;
  }

  async function fetchCart() {
    try {
      const res = await fetch('/cart.js', {
        method: 'GET',
        credentials: 'same-origin',
      });
      if (!res || !res.ok) return null;
      return await res.json();
    } catch (e) {
      console.error('[sampleGuard] Failed to fetch cart:', e);
      return null;
    }
  }

  async function changeLine(line, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: headersJSON,
        credentials: 'same-origin',
        body: JSON.stringify({ line, quantity }),
      });
      if (!res.ok) {
        console.warn('[sampleGuard] change.js failed for line', line, 'status', res.status);
      }
    } catch (e) {
      console.error('[sampleGuard] change.js error:', e);
    }
  }

  async function addVariant(variantId, quantity) {
    if (!variantId || !quantity) return;
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: headersJSON,
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [{ id: variantId, quantity }],
        }),
      });
      if (!res.ok) {
        console.warn(
          '[sampleGuard] add.js failed for variant',
          variantId,
          'qty',
          quantity,
          'status',
          res.status
        );
      }
    } catch (e) {
      console.error('[sampleGuard] add.js error:', e);
    }
  }

  /**
   * Core guard:
   * - If cart has free sample box but no subscription:
   *     remove free sample and add paid single box instead.
   * - If cart has included glasses but no subscription:
   *     remove those included glasses AND add their paid equivalents.
   *
   * @returns {Promise<boolean>} true if any changes were applied
   */
  async function ensureSampleGuard() {
    // Prevent overlapping runs that would double-add items
    if (guardInFlight) {
      console.info('[sampleGuard] Skipping run; guard already in flight.');
      return false;
    }

    guardInFlight = true;
    try {
      const cfg = loadGuardConfig();
      if (!cfg) {
        // No config yet (user might have gone straight to cart without seeing the section).
        return false;
      }

      const FREE_SAMPLE_VARIANT_ID = cfg.freeSampleVariantId;
      const PAID_SINGLE_BOX_VARIANT_ID = cfg.paidSampleVariantId;
      const SUBSCRIPTION_VARIANT_IDS = cfg.subscriptionVariantIds;
      const FREE_GLASS_VARIANT_IDS = Array.isArray(cfg.freeGlassVariantIds)
        ? cfg.freeGlassVariantIds
        : [];

      const cart = await fetchCart();
      if (!cart || !Array.isArray(cart.items)) return false;

      let hasSubscription = false;

      const freeSampleLines = [];
      let totalFreeSampleQty = 0;

      const freeGlassLines = [];
      let anyFreeGlass = false;

      // Track how many paid glasses to add per paid variant ID
      const glassSwapTotals = {};

      cart.items.forEach((item, index) => {
        const variantId = item.variant_id;
        const quantity = item.quantity || 0;
        const lineNumber = index + 1;

        if (SUBSCRIPTION_VARIANT_IDS.includes(variantId)) {
          hasSubscription = true;
        }

        if (variantId === FREE_SAMPLE_VARIANT_ID && quantity > 0) {
          freeSampleLines.push({ lineNumber, quantity });
          totalFreeSampleQty += quantity;
        }

        if (FREE_GLASS_VARIANT_IDS.includes(variantId) && quantity > 0) {
          freeGlassLines.push({ lineNumber, quantity, variantId });
          anyFreeGlass = true;

          const paidId = GLASS_MAPPING[String(variantId)] || GLASS_MAPPING[variantId];
          if (paidId) {
            if (!glassSwapTotals[paidId]) glassSwapTotals[paidId] = 0;
            glassSwapTotals[paidId] += quantity;
          }
        }
      });

      // If there is any subscription in the cart, do nothing.
      if (hasSubscription) {
        return false;
      }

      // If there are no guarded items (no free sample, no included glasses), do nothing.
      if (!freeSampleLines.length && !anyFreeGlass) {
        return false;
      }

      console.info(
        '[sampleGuard] Found guarded items (free sample and/or included glasses) without subscription. Adjusting cart…'
      );

      //
      // IMPORTANT: remove all free lines in a *single* pass from
      // highest lineNumber → lowest, so Shopify's re-indexing
      // never invalidates the remaining line indices.
      //
      const allLinesToRemove = [...freeSampleLines, ...freeGlassLines].sort(
        (a, b) => b.lineNumber - a.lineNumber
      );

      for (const line of allLinesToRemove) {
        await changeLine(line.lineNumber, 0);
      }

      // 3) Replace free sample quantity with paid single box, if any
      if (totalFreeSampleQty > 0) {
        await addVariant(PAID_SINGLE_BOX_VARIANT_ID, totalFreeSampleQty);
      }

      // 4) For each paid glass variant we accumulated, add the corresponding quantity
      const paidIds = Object.keys(glassSwapTotals);
      for (const paidIdStr of paidIds) {
        const paidId = parseInt(paidIdStr, 10);
        const qty = glassSwapTotals[paidIdStr];
        if (paidId && qty > 0) {
          await addVariant(paidId, qty);
        }
      }

      return true;
    } finally {
      guardInFlight = false;
    }
  }

  function initSampleGuard() {
    if (
      typeof fetch !== 'function' ||
      typeof window === 'undefined' ||
      typeof document === 'undefined'
    ) {
      return;
    }

    // Expose for cart.js (updateQuantity) to call after each cart change
    window.BlindBarrelsEnsureSampleGuard = ensureSampleGuard;

    // One initial cleanup in case user lands on cart with bad state.
    // Scoped to /cart only — prevents false-positive wipes on any other page.
    //
    // SKIP on checkout return: if the user just came back from Shopify checkout,
    // Bold's subscription variant may not be in the cart API response yet (it's
    // managed server-side by Bold), so the guard would incorrectly see "no subscription"
    // and delete legitimate glasses. Bobby confirmed: "same mechanism that removes
    // the discount in cart" — both fire on cart page load post-checkout.
    // Fix (2026-05-18): detect checkout referrer and skip initial guard run.
    var referrer = document.referrer || '';
    var fromCheckout = (
      referrer.indexOf('checkout.shopify.com') !== -1 ||
      referrer.indexOf('pay.shopify.com') !== -1 ||
      referrer.indexOf('/checkouts/') !== -1
    );

    if (window.location.pathname.startsWith('/cart') && !fromCheckout) {
      ensureSampleGuard();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSampleGuard);
  } else {
    initSampleGuard();
  }
})();
